<script>
    import { onMount } from 'svelte';
    import Select from 'svelte-select';

    import SHARED_CONFIG from 'configs/shared';
    import REGISTER_CONFIG from 'configs/register';

    import authStore from 'stores/auth';
    import loansStore from 'stores/loans';
    import registerStore from 'stores/register';

    import { createLoan } from 'services/infinity';

    import PageOverlay from 'components/PageOverlay';
    import LoanCalculator from 'components/LoanCalculator';

    export let trigger = false;
    export let onClose = () => {};
    export let onSubmit = () => {};

    const countryOptions = REGISTER_CONFIG.OPTIONS.COUNTRY;
    const locationOptions = REGISTER_CONFIG.OPTIONS.LOCATION;
    const platform = $registerStore.essentials.platform;
    const isStandardCredit = platform === SHARED_CONFIG.PLATFORMS.STANDARD_CREDIT;
    let country = countryOptions[0];
    let location = null;
    let activeLoan;
    let loan = {};

    $: canSubmit = !isStandardCredit || !!location;

    onMount(() => {
        activeLoan = getActiveLoan();
    });

    $: {
        if (trigger) {
            activeLoan = getActiveLoan();
        }
    }

    async function _onSubmit() {
        console.debug('Creating new loan ...');

        const countryKey = location ? `${country.value}_${location.value}` : country.value;

        await createLoan({
            loanType: SHARED_CONFIG.LOAN_TYPES.PERSONAL,
            customerId: $authStore.customerId,
            countryKey,
            loanAmount: loan.amount,
            refinanceId: activeLoan ? activeLoan.id : null,
            platform,
        });

        onSubmit();

        return Promise.resolve({});
    }

    function getActiveLoan() {
        return $loansStore.loans.active.length > 0 ? $loansStore.loans.active[0] : null;
    }
</script>

<PageOverlay
    title="Apply for Loan / Top-up"
    submitLabel="Apply for Loan"
    loadingLabel={'Applying for Loan ...'}
    {trigger}
    {canSubmit}
    onSubmit={_onSubmit}
    {onClose}
>
    <div slot="content">
        <form class="form">
            <div class="form__control">
                <label class="form__label" for="country">Select your Country</label>
                <div class="form__select">
                    <Select
                        items={countryOptions}
                        isClearable={false}
                        listPlacement="bottom"
                        bind:value={country}
                        on:select={(event) => {
                            country = event.detail;
                        }}
                        placeholder="Country"
                        showChevron={true}
                    />
                </div>
            </div>
            {#if isStandardCredit}
                <div class="form__control">
                    <label class="form__label" for="location">Where are you applying from?</label>
                    <div class="form__select">
                        <Select
                            items={locationOptions}
                            isClearable={false}
                            listPlacement="bottom"
                            bind:value={location}
                            on:select={(event) => {
                                location = event.detail;
                            }}
                            placeholder="Select a Branch"
                            showChevron={true}
                        />
                    </div>
                </div>
            {/if}
            <div class="form__control animate__animated animate__fadeIn">
                <LoanCalculator
                    country={SHARED_CONFIG.COUNTRIES[country.value]}
                    {activeLoan}
                    on:calculate={(event) => {
                        loan.amount = event.detail.borrowAmount;
                        loan.numPayments = event.detail.numPayments;
                        loan.reason = event.detail.loanReason;
                    }}
                />
            </div>
        </form>
    </div>
</PageOverlay>

<style lang="scss">
    @use 'src/sass/base' as *;

    .form {
        &__control {
            margin: em(25) 0;
        }

        &__label {
            font-weight: $fw-medium;
        }

        &__select {
            width: 100%;
            font-size: em(14);
            font-weight: $fw-regular;
            --inputPadding: 15px 20px;
            --border: 1px solid #c7c7ca;
            --borderFocusColor: #662cc8;
            --borderRadius: 8px;
            --itemHoverBG: #f4f4f4;
            --itemIsActiveBG: #662cc8;
            --multiItemActiveBG: #662cc8;
            --placeholderColor: #c7c7ca;
            --multiClearBG: #999999;
            --disabledBackground: #fbfbfb;
            --disabledBorderColor: #c7c7ca;
            --disabledColor: #000;
        }
    }
</style>
